import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import type { Patient } from "~/api/patients";
import { unassignPatientFromUnit } from "~/api/patients";
import { Button, Modal } from "~/ui";
import { errorToast, useToastStore } from "../../Shared/Toast";

interface RemovePatientConfirmationModalProps {
  isOpen: boolean;
  patient: Patient;
  unitId: number;
  onClose: () => void;
}

export function RemovePatientConfirmationModal({
  isOpen,
  patient,
  unitId,
  onClose,
}: RemovePatientConfirmationModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { pushToast } = useToastStore();

  const { mutate: unassignPatientMutation } = useMutation({
    mutationFn: unassignPatientFromUnit,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getUnitPatients", { unitId }],
      });
      onClose();
      void pushToast({
        type: "success",
        message: t("patients.remove_patient_success"),
      });
    },
    onError: (err) => {
      errorToast(err);
    },
  });

  const onSubmit = () => {
    unassignPatientMutation({
      patientId: patient.id,
      unitId: Number(unitId),
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("unit_detail.remove_patient")}
    >
      <p className="mb-8 text-left text-lg text-neutral-50 dark:text-neutral-80">
        {t("unit_detail.remove_confirmation_start")}
        <span className="font-bold">{`${patient.name} ${patient.surname}`}</span>
        {t("unit_detail.remove_confirmation_end")}
      </p>

      <div className="flex w-full gap-3">
        <Button
          variant="outline"
          onClick={onClose}
          text={t("general.cancel")}
          className="flex grow"
        />

        <Button
          variant="filled"
          onClick={onSubmit}
          text={t("general.remove")}
          className="flex grow"
        />
      </div>
    </Modal>
  );
}
